import { useEffect } from 'react';

/**
 * Integrates overlays (modal / bottom sheet) with browser & WebView back
 * navigation. When `isOpen` flips true, a history entry is pushed and a close
 * handler is added to a global LIFO stack so that a system back press
 * (Android hardware/gesture back, iOS gesture, native top-bar back arrow, or
 * browser back) closes the top-most overlay only. UI-driven closes pop the
 * matching history entry so the stack stays aligned.
 */

type Handler = () => void;

const overlayStack: Handler[] = [];
let suppressNextPop = 0;
let listenerInstalled = false;

function ensureListener() {
  if (listenerInstalled || typeof window === 'undefined') return;
  listenerInstalled = true;
  window.addEventListener('popstate', () => {
    if (suppressNextPop > 0) {
      suppressNextPop--;
      return;
    }
    const top = overlayStack.pop();
    if (top) top();
  });
}

export function useOverlayBackClose(isOpen: boolean, onClose: () => void) {
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    ensureListener();

    let closedByPopState = false;
    const handler: Handler = () => {
      closedByPopState = true;
      onClose();
    };

    overlayStack.push(handler);
    window.history.pushState({ overlay: true }, '');

    return () => {
      const idx = overlayStack.lastIndexOf(handler);
      if (idx !== -1) overlayStack.splice(idx, 1);

      if (!closedByPopState) {
        suppressNextPop++;
        window.history.back();
      }
    };
  }, [isOpen, onClose]);
}
